import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import { passThroughTranslation } from '@colonist/i18n';
import { Image } from '../../../Image/Image.tsx';
import { ScrollFadeContainer } from '../../../ScrollFadeContainer/ScrollFadeContainer.tsx';
import { T } from '../../../T/T.tsx';
import { prefersReducedMotion } from '../../../../utils/prefersReducedMotion.ts';
import styles from './Selector.module.scss';
const TAB_POSITION_SCROLL_DIRECTIONS = {
    top: 'horizontal',
    left: 'vertical'
};
const getIndicatorStyle = (tabPosition, activeButton, disableTransition)=>{
    const transitionStyle = disableTransition ? 'none' : undefined;
    const indicatorStyles = {
        top: {
            width: activeButton.offsetWidth,
            transform: `translateX(${activeButton.offsetLeft}px)`,
            transition: transitionStyle
        },
        left: {
            height: activeButton.offsetHeight,
            transform: `translateY(${activeButton.offsetTop}px)`,
            transition: transitionStyle
        }
    };
    return indicatorStyles[tabPosition];
};
const centerActiveTab = (tabPosition, tabBar, activeButton, disableSmoothScroll)=>{
    const shouldDisableSmoothScroll = disableSmoothScroll || prefersReducedMotion();
    const behavior = shouldDisableSmoothScroll ? 'auto' : 'smooth';
    const scrollOptions = {
        top: {
            left: activeButton.offsetLeft - (tabBar.clientWidth - activeButton.offsetWidth) / 2,
            behavior: behavior
        },
        left: {
            top: activeButton.offsetTop - (tabBar.clientHeight - activeButton.offsetHeight) / 2,
            behavior: behavior
        }
    };
    tabBar.scrollTo(scrollOptions[tabPosition]);
};
/** Scrollable tab bar with active indicator. */ export const Selector = ({ activeIndex, activeTabId, ref, onTabClick, tabPosition, tabs, variant })=>{
    const tabBarRef = useRef(null);
    const tabButtonRefs = useRef([]);
    const hasPositionedIndicatorRef = useRef(false);
    const hasCenteredActiveTabRef = useRef(false);
    const [indicatorStyle, setIndicatorStyle] = useState({});
    const showIcon = variant != 'textOnly';
    const showText = variant != 'iconOnly';
    const updateIndicator = useCallback(()=>{
        const activeButton = tabButtonRefs.current[activeIndex];
        if (activeButton == null) return;
        const isFirstPosition = !hasPositionedIndicatorRef.current;
        hasPositionedIndicatorRef.current = true;
        setIndicatorStyle(getIndicatorStyle(tabPosition, activeButton, isFirstPosition));
    }, [
        activeIndex,
        tabPosition
    ]);
    useLayoutEffect(()=>{
        updateIndicator();
        const observer = new ResizeObserver(updateIndicator);
        if (tabBarRef.current != null) observer.observe(tabBarRef.current);
        tabButtonRefs.current.forEach((button)=>{
            if (button != null) observer.observe(button);
        });
        return ()=>observer.disconnect();
    }, [
        updateIndicator,
        tabs
    ]);
    useEffect(()=>{
        const tabBar = tabBarRef.current;
        if (tabBar == null) return;
        const activeButton = tabButtonRefs.current[activeIndex];
        if (activeButton == null) return;
        const isFirstCenter = !hasCenteredActiveTabRef.current;
        hasCenteredActiveTabRef.current = true;
        centerActiveTab(tabPosition, tabBar, activeButton, isFirstCenter);
    }, [
        activeIndex,
        tabPosition
    ]);
    return /*#__PURE__*/ React.createElement(ScrollFadeContainer, {
        ref: tabBarRef,
        direction: TAB_POSITION_SCROLL_DIRECTIONS[tabPosition],
        hideScrollbar: true
    }, /*#__PURE__*/ React.createElement("div", {
        ref: ref,
        className: clsx(styles.tabBar, styles[variant], styles[tabPosition])
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.indicator,
        style: indicatorStyle
    }), tabs.map((tab, index)=>{
        const { id, label, icon } = tab.props;
        return /*#__PURE__*/ React.createElement("button", {
            key: id,
            ref: (el)=>{
                tabButtonRefs.current[index] = el;
            },
            type: "button",
            className: styles.tab,
            "aria-pressed": id == activeTabId,
            onClick: ()=>onTabClick(id, index)
        }, showIcon && icon != undefined && /*#__PURE__*/ React.createElement(Image, {
            className: styles.icon,
            image: {
                src: icon,
                alt: label !== null && label !== void 0 ? label : passThroughTranslation('')
            }
        }), showText && label != undefined && /*#__PURE__*/ React.createElement("span", {
            className: styles.label
        }, /*#__PURE__*/ React.createElement(T, {
            id: label
        })));
    })));
};
